import { Logo } from "../components/Logo";
import { SITE_ORIGIN } from "../seo/site";

import { HANDOUT_NOTES, HANDOUT_STEPS, sceneSrc } from "./content";

const SITE_HOST = new URL(SITE_ORIGIN).host;

/**
 * 인쇄용 학생 안내문 A4 한 장. 공개 안내문은 아이디·비밀번호 칸을 비워 두고,
 * 학급 안내문은 학생마다 두 칸을 채워 한 장씩 찍는다. 공통 처음 비밀번호를
 * 공개 페이지에 적지 않도록 기본값을 두지 않는다.
 */
export const HandoutSheet = ({
  owner,
  loginId,
  password,
}: {
  /** 머리에 적는 학급과 번호. 공개 안내문에는 없다. */
  owner?: string;
  /** 비우면 선생님이 손으로 적는 빈칸이 된다. */
  loginId?: string;
  /** 비우면 선생님이 손으로 적는 빈칸이 된다. */
  password?: string;
}) => (
  <article
    className="ch-sheet"
    aria-label={owner ? `${owner} 안내문` : "학생용 안내문"}
  >
    <header className="ch-head">
      <div>
        {owner && <p className="ch-owner">{owner}</p>}
        <h1>크루바스에서 그림 그리기</h1>
        <p>선생님이 만든 캔버스에 내 칸이 있어요.</p>
      </div>
      <span className="ch-brand">
        <Logo size={24} />
        크루바스
      </span>
    </header>

    <dl className="ch-account">
      <div>
        <dt>접속 주소</dt>
        <dd>{SITE_HOST}</dd>
      </div>
      <div>
        <dt>내 아이디</dt>
        {loginId ? (
          <dd className="ch-login-id">{loginId}</dd>
        ) : (
          <dd className="ch-blank" aria-label="빈칸: 선생님이 적어 줍니다" />
        )}
      </div>
      <div>
        <dt>처음 비밀번호</dt>
        {password ? (
          <dd>{password}</dd>
        ) : (
          <dd className="ch-blank" aria-label="빈칸: 선생님이 적어 줍니다" />
        )}
      </div>
    </dl>

    <ol className="ch-steps">
      {HANDOUT_STEPS.map((step, index) => (
        <li key={step.image}>
          <img
            src={sceneSrc(step.image)}
            alt=""
            width={640}
            height={400}
            decoding="async"
          />
          <h2>
            <span className="ch-step-number">{index + 1}</span>
            {step.title}
          </h2>
          <p>{step.body}</p>
        </li>
      ))}
    </ol>

    <ul className="ch-notes">
      {HANDOUT_NOTES.map((note) => (
        <li key={note}>{note}</li>
      ))}
    </ul>
  </article>
);

/**
 * 안내문이 화면에 있는 동안만 쓰는 인쇄 규칙. 명단 인쇄 같은 다른 화면은 받지
 * 않는다.
 * - 종이 여백 0: 이름 붙은 @page는 브라우저마다 지원이 달라 기본 여백이 남으면
 *   한 장이 두 쪽으로 밀리므로 쓰지 않는다.
 * - index.html이 html·body·#root를 화면 높이에 묶고 안에서 스크롤하게 해 두어서,
 *   그대로 찍으면 첫 화면 높이만큼 한 쪽만 나온다. 인쇄 때는 그 묶음을 푼다.
 */
const HANDOUT_PRINT_RULE = `
@page { size: A4 portrait; margin: 0; }
@media print {
  html, body, #root {
    height: auto !important;
    overflow: visible !important;
    margin: 0 !important;
    padding: 0 !important;
  }
}
`;

export const HandoutPageRule = () => <style>{HANDOUT_PRINT_RULE}</style>;

/**
 * 안내문의 그림이 모두 들어올 때까지 기다린다. 덜 받은 그림은 빈칸으로 찍힌다.
 * 망이 끊겨 그림 하나가 멈춰도 인쇄는 되도록 `limitMs` 뒤에는 그냥 넘어간다.
 */
export const whenImagesReady = (
  root: ParentNode,
  limitMs = 8000,
): Promise<void> =>
  Promise.race([
    Promise.all(
      Array.from(root.querySelectorAll("img"), (image) =>
        image.complete
          ? Promise.resolve()
          : new Promise<void>((resolve) => {
              image.addEventListener("load", () => resolve(), { once: true });
              image.addEventListener("error", () => resolve(), { once: true });
            }),
      ),
    ).then(() => undefined),
    new Promise<void>((resolve) => window.setTimeout(resolve, limitMs)),
  ]);
